import _slicedToArray from "/home/bigstep/Public/googlemediapipe/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { tensor1d } from '../tensor1d';
import { op } from '../operation';
import { cast } from '../cast';
import { split } from '../split';
import { bincount } from '../bincount';
import { lessEqual } from '../less_equal';
import { greater } from '../greater';
import { sum } from '../sum';
import { add } from '../add';
import { mul } from '../mul';
import { div } from '../div';
import { sub } from '../sub';
import { round } from '../round';
import { where } from '../where';
import { fill } from '../fill';
import { slice } from '../slice';
import { range } from '../range';
import { tensor } from '../tensor';
import * as util from '../../util';
import { convertToTensor } from '../../tensor_util_env';
/**
 * Performs image binarization with corresponding threshold
 * (depends on the method)value, which creates a binary image from a grayscale.
 * @param image 3d tensor of shape [imageHeight,imageWidth, depth],
 * where imageHeight and imageWidth must be positive.The image color
 * range should be [0, 255].
 * @param method Optional string from `'binary' | 'otsu'`
 * which specifies the method for thresholding. Defaults to 'binary'.
 * @param inverted Optional boolean whichspecifies
 * if colours should be inverted. Defaults to false.
 * @param threshValue Optional number which defines threshold value from 0 to 1.
 * Defaults to 0.5.
 * @return A 3d tensor of shape [imageHeight,imageWidth, depth], which
 * contains binarized image.
 */
function threshold_(image) {
  var method = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'binary';
  var inverted = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var threshValue = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0.5;
  var $image = convertToTensor(image, 'image', 'threshold');
  /* 0.2989, 0.5870, 0.1140 are represent luma coefficients in CCIR601.
  Reference for converting between RGB and grayscale: https://en.wikipedia.org/wiki/Luma_%28video%29  */
  var RED_INTENCITY_COEF = 0.2989;
  var GREEN_INTENCITY_COEF = 0.5870;
  var BLUE_INTENCITY_COEF = 0.1140;
  var totalPixelsInImage = $image.shape[0] * $image.shape[1];
  var $threshold = mul(tensor1d([threshValue]), 255);
  var r, g, b, grayscale;
  util.assert($image.rank === 3, function () {
    return 'Error in threshold: image must be rank 3,' + "but got rank ".concat($image.rank, ".");
  });
  util.assert($image.shape[2] === 3 || $image.shape[2] === 1, function () {
    return 'Error in threshold: ' + 'image color channel must be equal to 3 or 1' + "but got ".concat($image.shape[2], ".");
  });
  util.assert($image.dtype === 'int32' || $image.dtype === 'float32', function () {
    return 'Error in dtype: image dtype must be int32 or float32,' + "but got dtype ".concat($image.dtype, ".");
  });
  util.assert(method === 'otsu' || method === 'binary', function () {
    return "Method must be binary or otsu, but was ".concat(method);
  });
  if ($image.shape[2] === 3) {
    var _split = split($image, [1, 1, 1], -1);
    var _split2 = _slicedToArray(_split, 3);
    r = _split2[0];
    g = _split2[1];
    b = _split2[2];
    var $r = mul(r, RED_INTENCITY_COEF);
    var $g = mul(g, GREEN_INTENCITY_COEF);
    var $b = mul(b, BLUE_INTENCITY_COEF);
    grayscale = add(add($r, $g), $b);
  } else {
    grayscale = image;
  }
  if (method === 'otsu') {
    var $histogram = bincount(cast(round(grayscale), 'int32'), tensor([]), 256);
    $threshold = otsu($histogram, totalPixelsInImage);
  }
  var invCondition = inverted ? lessEqual(grayscale, $threshold) : greater(grayscale, $threshold);
  var result = cast(mul(invCondition, 255), 'int32');
  return result;
}
function otsu(histogram, total) {
  var bestThresh = tensor1d([-1]);
  var bestInBetVar = tensor1d([0]);
  var cInBetVar = tensor1d([0]);
  var classFirst, classSecond, meanFirst, meanSec, weightForeground, weightBack;
  for (var index = 0; index < histogram.size - 1; index++) {
    classFirst = slice(histogram, 0, index + 1);
    classSecond = slice(histogram, index + 1);
    weightForeground = div(sum(classFirst), total);
    weightBack = div(sum(classSecond), total);
    var meanFirstDivA = sum(mul(classFirst, range(0, classFirst.size)));
    meanFirst = div(meanFirstDivA, sum(classFirst));
    var meanSecFill = fill(classSecond.shape, classFirst.size);
    var meanSecAdd = add(range(0, classSecond.size), meanSecFill);
    var meanSecMul = mul(classSecond, meanSecAdd);
    meanSec = div(sum(meanSecMul), sum(classSecond));
    var cInBetVarSubA = sub(meanFirst, meanSec);
    var cInBetVarSubB = sub(meanFirst, meanSec);
    var cInBetVarMul = mul(weightForeground, weightBack);
    cInBetVar = mul(mul(cInBetVarMul, cInBetVarSubA), cInBetVarSubB);
    var condition = greater(cInBetVar, bestInBetVar);
    bestInBetVar = where(condition, cInBetVar, bestInBetVar);
    bestThresh = where(condition, tensor1d([index]), bestThresh);
  }
  return bestThresh;
}
export var threshold = op({
  threshold_: threshold_
});